import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@atlaskit/tooltip';
import { routePaths } from '@css-xp/ppc-constants';
import { getCompanyNameRoute, getRouteUrl } from '@css-xp/ppc-utils';

export interface SoldToAddressDetailsLinkProps {
  txaId: string | undefined;
  // pass the shipToId to generate ship-to id related link /customer-details/<companyName>
  shipToId?: string | undefined | null;
  // pass the companyName to generate company name related link /customers/<companyName>
  companyName?: string;
  children?: string | ReactElement;
  shouldRemoveLineClamp?: boolean;
  shouldDisableLink?: boolean;
}

/**
 * Use to generate the link for the Sold-to address and company name
 * @param children
 * @param txaId
 * @param shipToId pass the shipToId to generate ship-to id related link
 * @param companyName pass the companyName to generate company name related link
 * @param shouldRemoveLineClamp remove the line clamp class
 * @constructor
 */
export const SoldToAddressDetailsLink = ({
  children,
  txaId,
  shipToId,
  companyName,
  shouldRemoveLineClamp,
  shouldDisableLink,
}: SoldToAddressDetailsLinkProps) => {
  // Classes for truncating after multiple lines with ellipsis
  const defaultStyleClass = shouldRemoveLineClamp ? '' : 'line-clamp';

  if (companyName && shipToId) {
    throw new Error('The component expects only one prop out of company name and ship-to id for link generation');
  }

  if (!txaId || !(shipToId || companyName) || shouldDisableLink) {
    return (
      <Tooltip content={children} tag="span" position="bottom-start">
        <span className={defaultStyleClass} data-testid={`sold-to-address-${companyName || shipToId}`}>
          {children}
        </span>
      </Tooltip>
    );
  }

  const getLinkURL = () => {
    if (companyName) {
      return getCompanyNameRoute({ companyName, txaId });
    }
    if (shipToId) {
      return getRouteUrl(routePaths.customerAddressDetailsById, {
        txaId: txaId,
        addressId: shipToId,
      });
    }
  };

  const linkURL = getLinkURL();

  return (
    <Tooltip content={children} tag="span" position="bottom-start">
      <Link
        className={`${defaultStyleClass} site-name-link`}
        data-testid={`ship-to-id-${companyName || shipToId}`}
        to={linkURL}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </Link>
    </Tooltip>
  );
};
